import { Stack, Text, Group, Badge, Button, Code, Divider, Box } from '@mantine/core'
import { ResizableDrawer } from '@/components/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { IconCheck, IconEyeOff, IconHandStop } from '@tabler/icons-react'
import { securityApi, type SecurityFinding } from '@/api/security'
import { useCurrentTenant } from '@/store/auth'
import { SeverityBadge } from './SeverityBadge'

interface FindingDrawerProps {
  finding: SecurityFinding | null
  opened: boolean
  onClose: () => void
}

export function FindingDrawer({ finding, opened, onClose }: FindingDrawerProps) {
  const tenant = useCurrentTenant()
  const qc = useQueryClient()

  const mutation = useMutation({
    mutationFn: (action: string) => {
      if (!tenant || !finding) throw new Error('Missing context')
      return securityApi.updateFinding(tenant.id, finding.id, action)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['security'] })
      onClose()
    },
  })

  if (!finding) return null

  return (
    <ResizableDrawer
      storageKey="finding-drawer-width"
      defaultWidth={600}
      minWidth={400}
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Text fw={700} size="lg">Finding Detail</Text>
          <SeverityBadge severity={finding.severity} />
        </Group>
      }
      position="right"
      padding="lg"
    >
      <Stack gap="md">
        <Box>
          <Text size="sm" c="dimmed">Title</Text>
          <Text fw={600}>{finding.title}</Text>
        </Box>

        <Group grow>
          <Box>
            <Text size="sm" c="dimmed">Category</Text>
            <Badge variant="light" color="blue">{finding.category}</Badge>
          </Box>
          <Box>
            <Text size="sm" c="dimmed">Check ID</Text>
            <Text size="sm" ff="monospace">{finding.check_id}</Text>
          </Box>
        </Group>

        <Group grow>
          <Box>
            <Text size="sm" c="dimmed">Status</Text>
            <Badge variant="outline">{finding.status}</Badge>
          </Box>
          <Box>
            <Text size="sm" c="dimmed">Region</Text>
            <Text size="sm">{finding.region || 'N/A'}</Text>
          </Box>
        </Group>

        <Box>
          <Text size="sm" c="dimmed">Resource ARN</Text>
          <Code block style={{ wordBreak: 'break-all' }}>
            {finding.resource_arn || 'N/A'}
          </Code>
        </Box>

        <Box>
          <Text size="sm" c="dimmed">Resource Type</Text>
          <Text size="sm">{finding.resource_type || 'N/A'}</Text>
        </Box>

        <Box>
          <Text size="sm" c="dimmed">First Seen</Text>
          <Text size="sm">{new Date(finding.first_seen_at).toLocaleString()}</Text>
        </Box>

        {finding.resolved_at && (
          <Box>
            <Text size="sm" c="dimmed">Resolved At</Text>
            <Text size="sm">{new Date(finding.resolved_at).toLocaleString()}</Text>
          </Box>
        )}

        <Divider />

        <Box>
          <Text size="sm" fw={600} mb="xs">Remediation</Text>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {finding.remediation || 'No remediation guidance available.'}
          </Text>
        </Box>

        {finding.details && (
          <Box>
            <Text size="sm" fw={600} mb="xs">Details</Text>
            <Code block style={{ maxHeight: 200, overflowY: 'auto' }}>
              {typeof finding.details === 'string' ? finding.details : JSON.stringify(finding.details, null, 2)}
            </Code>
          </Box>
        )}

        <Divider />

        <Text size="sm" fw={600}>Actions</Text>
        <Group>
          <Button
            size="sm"
            variant="light"
            color="teal"
            leftSection={<IconCheck size={16} />}
            loading={mutation.isPending}
            onClick={() => mutation.mutate('resolve')}
          >
            Resolve
          </Button>
          <Button
            size="sm"
            variant="light"
            color="blue"
            leftSection={<IconHandStop size={16} />}
            loading={mutation.isPending}
            onClick={() => mutation.mutate('acknowledge')}
          >
            Acknowledge
          </Button>
          <Button
            size="sm"
            variant="light"
            color="gray"
            leftSection={<IconEyeOff size={16} />}
            loading={mutation.isPending}
            onClick={() => mutation.mutate('suppress')}
          >
            Suppress
          </Button>
        </Group>
      </Stack>
    </ResizableDrawer>
  )
}
